import { Client, type Integration, type Bot } from '@botpress/client'
import { AgentProject } from '../agent-project/agent-project.js'
import { auth } from '../auth/index.js'
import { IntegrationManager } from './manager.js'
import type { ParsedIntegration } from './types.js'

export type IntegrationInstallStatus = 'enabled' | 'not_installed' | 'disabled'
export type IntegrationRegistrationStatus =
  | 'registered'
  | 'registration_pending'
  | 'registration_failed'
  | 'unregistration_pending'
  | 'unregistered'
  | 'unregistration_failed'

export interface IntegrationCheckResult {
  alias: string
  name: string

  // Current state (from bot)
  installStatus: IntegrationInstallStatus
  registrationStatus?: IntegrationRegistrationStatus
  installedVersion?: string
  currentConfigurationType?: string | null
  currentConfiguration?: Record<string, any>

  // Desired state (from agent.config.ts dependencies)
  desiredInstallStatus: IntegrationInstallStatus
  desiredVersion: string
  desiredIntegrationId?: string // The integration ID for the desired version
  desiredConfigurationType?: string | null
  desiredConfiguration?: Record<string, any>

  // Integration definition
  definition: Integration

  // Flags for easy checking
  needsInstall: boolean
  needsUpdate: boolean
  needsRemoval: boolean
  needsConfiguration: boolean
  hasError: boolean
  errorMessage?: string
}

export class IntegrationChecker {
  private projectPath: string
  private client?: Client

  constructor(projectPath: string) {
    this.projectPath = projectPath
  }

  private async getClient(): Promise<Client> {
    if (!this.client) {
      const credentials = await auth.getActiveCredentials()
      const project = await AgentProject.load(this.projectPath)

      const workspaceId = project.agentInfo?.workspaceId || credentials.workspaceId

      if (!workspaceId) {
        throw new Error('No workspace ID found. Please login with "adk login"')
      }

      this.client = new Client({
        token: credentials.token,
        apiUrl: credentials.apiUrl,
        workspaceId,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })
    }
    return this.client
  }

  /**
   * Fetch integration definition from Botpress API
   * Try private first, then public
   */
  private async fetchIntegrationDefinition(name: string, version: string): Promise<Integration> {
    const client = await this.getClient()

    // Try both private and public in parallel, prefer private
    const [privateResult, publicResult] = await Promise.allSettled([
      client.getIntegrationByName({ name, version }).then(({ integration }) => integration as Integration),
      client.getPublicIntegration({ name, version }).then(({ integration }) => integration as Integration),
    ])

    // Prefer private integration
    if (privateResult.status === 'fulfilled') {
      return privateResult.value
    }

    if (publicResult.status === 'fulfilled') {
      return publicResult.value
    }

    // Both failed
    throw new Error(
      `Failed to fetch integration ${name}@${version}: ${
        privateResult.status === 'rejected' ? privateResult.reason : 'Unknown error'
      }`
    )
  }

  /**
   * Check integration status for a specific bot
   */
  async checkIntegrations(botId: string): Promise<IntegrationCheckResult[]> {
    const client = await this.getClient()
    const project = await AgentProject.load(this.projectPath)

    // Load desired state from agent.config.ts dependencies
    const integrationManager = new IntegrationManager({
      workspaceId: project.agentInfo?.workspaceId,
    })
    const loadResult = await integrationManager.loadIntegrations(project.dependencies || { integrations: {} })

    if (loadResult.errors.length > 0) {
      throw new Error(`Failed to load integrations: ${loadResult.errors.map((e) => e.message).join(', ')}`)
    }

    // Get current bot state
    let bot: Bot
    try {
      const response = await client.getBot({ id: botId })
      bot = response.bot
    } catch (error) {
      throw new Error(`Failed to get bot: ${error instanceof Error ? error.message : String(error)}`)
    }

    const results: IntegrationCheckResult[] = []
    const installedIntegrations = bot.integrations || {}

    // Track which installed integrations we've processed
    const processedIntegrationIds = new Set<string>()

    // Process each integration from agent.config.ts dependencies
    for (const parsedIntegration of loadResult.integrations) {
      const result = await this.checkIntegration(parsedIntegration, installedIntegrations, processedIntegrationIds)
      results.push(result)
    }

    // Check for extra integrations that are installed but not in dependencies
    for (const [key, installedIntegration] of Object.entries(installedIntegrations)) {
      if (!processedIntegrationIds.has(installedIntegration.id)) {
        // This integration is installed but not in dependencies - should be removed
        // We need to fetch the full integration definition
        try {
          const integrationDef = await this.fetchIntegrationDefinition(
            installedIntegration.name,
            installedIntegration.version
          )

          results.push({
            alias: key,
            name: installedIntegration.name,
            installStatus: installedIntegration.enabled ? 'enabled' : 'disabled',
            registrationStatus: installedIntegration.status,
            installedVersion: installedIntegration.version,
            currentConfigurationType: installedIntegration.configurationType,
            currentConfiguration: installedIntegration.configuration,
            desiredInstallStatus: 'not_installed',
            desiredVersion: installedIntegration.version,
            definition: integrationDef,
            needsInstall: false,
            needsUpdate: false,
            needsRemoval: true,
            needsConfiguration: false,
            hasError: false,
          })
        } catch {
          // If we can't fetch the definition, skip this integration
          continue
        }
      }
    }

    return results
  }

  private async checkIntegration(
    parsedIntegration: ParsedIntegration,
    installedIntegrations: Record<string, any>,
    processedIntegrationIds: Set<string>
  ): Promise<IntegrationCheckResult> {
    const { alias, ref, enabled, configurationType, config, definition } = parsedIntegration

    if (!definition) {
      throw new Error(`Integration definition not found for ${alias}`)
    }

    // Find matching installed integration by name (regardless of version)
    let installedIntegration: any = null

    for (const [_key, installed] of Object.entries(installedIntegrations)) {
      // Compare by name, handling both workspace-scoped and public integrations
      // installed.name from API might be "intercom" while ref.fullName is "sly/intercom"
      // So we need to check both the full name and just the base name
      const matches =
        installed.name === ref.fullName || // Exact match (e.g., "sly/intercom" === "sly/intercom")
        installed.name === ref.name // Base name match (e.g., "intercom" === "intercom")

      if (matches) {
        installedIntegration = installed
        processedIntegrationIds.add(installed.id)
        break
      }
    }

    // Determine desired install status
    const desiredInstallStatus: IntegrationInstallStatus = enabled === false ? 'disabled' : 'enabled'

    // Build result
    const result: IntegrationCheckResult = {
      alias,
      name: ref.fullName,
      installStatus: installedIntegration ? (installedIntegration.enabled ? 'enabled' : 'disabled') : 'not_installed',
      desiredInstallStatus,
      installedVersion: installedIntegration?.version,
      desiredVersion: ref.version,
      desiredIntegrationId: definition.id, // The integration ID for the desired version
      currentConfigurationType: installedIntegration?.configurationType,
      currentConfiguration: installedIntegration?.configuration,
      desiredConfigurationType: configurationType ?? null,
      desiredConfiguration: config,
      definition,
      needsInstall: false,
      needsUpdate: false,
      needsRemoval: false,
      needsConfiguration: false,
      hasError: false,
    }

    // Set registration status if installed
    if (installedIntegration) {
      result.registrationStatus = installedIntegration.status

      // Check for registration errors
      if (installedIntegration.status === 'registration_failed' && installedIntegration.statusReason) {
        result.hasError = true
        result.errorMessage = installedIntegration.statusReason
      }
    }

    // Determine what needs to be done
    if (!installedIntegration) {
      // Not installed - needs installation
      result.needsInstall = true
      if (config) {
        result.needsConfiguration = true
      }
    } else {
      // Already installed - check if update needed
      if (installedIntegration.version !== ref.version) {
        result.needsUpdate = true
      }

      // Check if configuration type changed (only if explicitly specified in dependencies)
      if (configurationType !== undefined && configurationType !== installedIntegration.configurationType) {
        result.needsConfiguration = true
      }

      // Check if configuration changed (only if explicitly specified in dependencies)
      if (config !== undefined) {
        // Get the schema for the configuration type to know which fields to compare
        const configType = configurationType ?? installedIntegration.configurationType ?? 'default'
        const schema =
          configType === 'default' ? definition.configuration?.schema : definition.configurations?.[configType]?.schema

        // Get schema properties (fields that should be compared)
        const schemaProperties = schema?.properties || {}
        const propertyKeys = Object.keys(schemaProperties)

        // Filter both configs to only include properties defined in the schema
        const filterConfig = (cfg: Record<string, any>) => {
          const filtered: Record<string, any> = {}
          for (const key of propertyKeys) {
            if (key in cfg) {
              filtered[key] = cfg[key]
            }
          }
          return filtered
        }

        const currentConfig = filterConfig(installedIntegration.configuration || {})
        const desiredConfig = filterConfig(config || {})

        // Only flag as needing configuration if the schema-defined configs are different
        if (JSON.stringify(currentConfig) !== JSON.stringify(desiredConfig)) {
          result.needsConfiguration = true
        }
      }

      // Check if enabled status changed (only if explicitly specified in dependencies)
      if (enabled !== undefined) {
        if (
          (enabled === false && installedIntegration.enabled) ||
          (enabled === true && !installedIntegration.enabled)
        ) {
          result.needsConfiguration = true
        }
      }
    }

    return result
  }
}
